import {
  Button,
  Input,
  InputGroup,
  type SelectOption,
  toast,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { type ImageryLayer } from 'cesium';
import { isEmpty, isNil, snakeCase } from 'lodash';
import { MapTool } from 'map-3d/components/map-3d-container/map-3d-tools';
import {
  MarkArea,
  MarkAreaToggle,
  type WorkspaceLayer,
} from 'map-3d/components/map-3d-sidebar/shared';
import { FormErrorMessage } from 'map-3d/components/map-3d-sidebar/shared/enums';
import { PolygonEventType } from 'map-3d/shared';
import {
  selectActiveWorkspaceLayersByFeatureType,
  selectMap3dDataset,
  selectMap3DState,
  setCurrentActiveMapTool,
  setOverlayDirty,
} from 'map-3d/shared/map-3d-slices';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from 'shared/analytics';
import {
  type CreateTerrainCleanupPayload,
  FeatureGeometryType,
  handleResponseErrorMessage,
  useCreateTerrainCleanup,
} from 'shared/api';
import { ErrorSlugs, EventPropertyKey, Events } from 'shared/enums';
import {
  loadWorkspaceLayerAsVectorTile,
  removeVectorTileLayer,
} from 'shared/helpers';
import { useInputFields } from 'shared/hooks';
import { cleanupFormInitialState, polygonLayersTemplate } from './constants';
import { type TerrainCleanupFormType } from './types';

type TerrainCleanupFormProps = {
  setIsGeneratingCleanup: (state: boolean) => void;
};

// Constants.
const AOI_OUTSIDE_TERRAIN_COVERAGE_MESSAGE =
  'Selected area is outside the terrain coverage. Please select an area that overlaps the terrain.';

export const TerrainCleanupForm: React.FC<TerrainCleanupFormProps> = ({
  setIsGeneratingCleanup,
}) => {
  // States.
  const [hasPolygons, setHasPolygons] = useState<boolean>(false);
  const [currentMarkedArea, setCurrentMarkedArea] = useState<MarkArea>(
    MarkArea.Draw,
  );
  const [isDrawPolygonToolEnabled, setIsDrawPolygonToolEnabled] =
    useState<boolean>(false);

  const loadedImageryLayer = useRef<ImageryLayer | null>(null);

  // Selectors.
  const { selectedTerrainIteration, selectedTerrainSite, selectedProject } =
    useAppSelector(selectMap3dDataset);
  const { cesiumProxy, aoiManager } = useAppSelector(selectMap3DState);

  const activeLayers = useAppSelector((state) =>
    selectActiveWorkspaceLayersByFeatureType(
      state,
      FeatureGeometryType.Polygon,
    ),
  );

  // Drawing tools.
  const polygonTool = aoiManager?.polygonTool;

  const polygonLayersSelectOptions: SelectOption<WorkspaceLayer>[] =
    activeLayers.map((activeLayer) => ({
      label: activeLayer.name,
      value: activeLayer,
    }));

  // Dispatch.
  const dispatch = useAppDispatch();

  // Hooks.
  const {
    values,
    setValues,
    names,
    errors,
    setErrors,
    onChange,
    onBlur,
    onFocus,
  } = useInputFields<TerrainCleanupFormType>(cleanupFormInitialState);

  // APIs.
  const {
    mutate: sendCreateTerrainCleanupRequest,
    isSuccess: isSuccessCreateCleanup,
    isError: isErrorCreateCleanup,
    data: createCleanupResponse,
    error: createCleanupError,
    isPending: isCreatingTerrainCleanup,
  } = useCreateTerrainCleanup();

  // Effects.
  useEffect(() => {
    return () => {
      removeLoadedImageryLayer();
      endDrawingHandler();
      dispatch(setOverlayDirty(false));
    };
  }, []);

  useEffect(() => {
    dispatch(
      setOverlayDirty(
        !isEmpty(values.cleanupName) ||
          hasPolygons ||
          !isNil(values.workspaceLayer),
      ),
    );
  }, [values.cleanupName, values.workspaceLayer, hasPolygons]);

  useEffect(() => {
    if (!isSuccessCreateCleanup) {
      return;
    }

    toast.success(createCleanupResponse.meta?.message);
    resetDrawingHandler();
    setValues(cleanupFormInitialState);
    setIsGeneratingCleanup(true);
  }, [isSuccessCreateCleanup, createCleanupResponse]);

  useEffect(() => {
    if (!isErrorCreateCleanup || isNil(createCleanupError)) {
      return;
    }

    const aoiOutsideError =
      createCleanupError.meta.details?.['aoi_geometry']?.[0]?.slug;

    if (aoiOutsideError === ErrorSlugs.AoiOutsideTerrainCoverage) {
      toast.error(AOI_OUTSIDE_TERRAIN_COVERAGE_MESSAGE);
      return;
    }

    handleResponseErrorMessage(isErrorCreateCleanup, createCleanupError);
  }, [isErrorCreateCleanup, createCleanupError]);

  useEffect(() => {
    if (isNil(values.workspaceLayer)) {
      return;
    }

    // Remove previously loaded layer if it exists and load the new layer
    removeLoadedImageryLayer();
    loadPolygonsFromWorkspace();
  }, [values.workspaceLayer]);

  // Handlers.
  const loadPolygonsFromWorkspace = async () => {
    if (isNil(cesiumProxy) || isNil(values.workspaceLayer)) {
      return;
    }

    // Load the selected layer as vector tiles
    const imageryLayer = await loadWorkspaceLayerAsVectorTile(
      values.workspaceLayer.value,
      cesiumProxy,
      polygonLayersTemplate,
    );

    if (imageryLayer) {
      loadedImageryLayer.current = imageryLayer;
      aoiManager?.addImageryLayer(imageryLayer);
    }
  };

  const deletePolygonsByMarkedArea = (markArea: MarkArea) => {
    if (markArea === MarkArea.LayerFromWorkspace) {
      removeLoadedImageryLayer();
    } else {
      polygonTool?.resetCurrentDrawnArray(false);
      setHasPolygons(false);
    }
  };

  const onToggleChange = (toggledLabel: string) => {
    const toggledValue = snakeCase(toggledLabel) as MarkArea;

    if (toggledValue === MarkArea.Draw) {
      // Handle switching to Draw mode.
      deletePolygonsByMarkedArea(MarkArea.LayerFromWorkspace);

      setErrors({
        ...errors,
        workspaceLayer: '',
      });

      setValues({
        ...values,
        workspaceLayer: null,
      });
    } else {
      // Handle switching to Workspace Layer mode.
      deletePolygonsByMarkedArea(MarkArea.Draw);
      setIsDrawPolygonToolEnabled(false);

      polygonTool?.deactivate();

      if (
        isNil(polygonLayersSelectOptions) ||
        isEmpty(polygonLayersSelectOptions)
      ) {
        setErrors({
          ...errors,
          workspaceLayer: FormErrorMessage.NoActiveWorkspaceLayer,
        });
      }
    }

    setCurrentMarkedArea(toggledValue);
  };

  const polygonCreatedHandler = () => {
    setHasPolygons(true);
  };

  const startDrawingHandler = () => {
    polygonTool?.activate({
      terrainCleanup: MarkArea.Draw,
    });
    polygonTool?.addEventListener(
      PolygonEventType.PolygonCreated,
      polygonCreatedHandler,
    );
    dispatch(setCurrentActiveMapTool(MapTool.None));
    setIsDrawPolygonToolEnabled(true);
  };

  const endDrawingHandler = () => {
    polygonTool?.deactivate();
    polygonTool?.removeEventListener(
      PolygonEventType.PolygonCreated,
      polygonCreatedHandler,
    );
    setIsDrawPolygonToolEnabled(false);
  };

  const resetDrawingHandler = () => {
    polygonTool?.deleteAllCurrentPolygons();
    setHasPolygons(false);
  };

  const removeLoadedImageryLayer = () => {
    if (isNil(cesiumProxy) || isNil(loadedImageryLayer.current)) {
      return;
    }

    aoiManager?.removeImageryLayer(loadedImageryLayer.current);
    removeVectorTileLayer(loadedImageryLayer.current, cesiumProxy);
    loadedImageryLayer.current = null;
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const payload: CreateTerrainCleanupPayload = {
      name: values.cleanupName,
      iterationId: selectedTerrainIteration?.id || '',
    };
    let polygonCount: number = 0;

    if (currentMarkedArea === MarkArea.LayerFromWorkspace) {
      // For workspace layer mode, send layerId.
      if (!values.workspaceLayer?.value?.id) {
        toast.error('Please select a workspace layer');
        return;
      }

      payload.layerId = values.workspaceLayer.value.id;
      polygonCount =
        values.workspaceLayer?.value.properties?.featuresCount?.polygons || 0;
    } else {
      // For draw mode, send aoiGeometry.
      // Get all polygon IDs based on the current marked area.
      const polygonIds = polygonTool?.currentDrawnPolygonIds || [];

      // Validating that polygonIds are not empty.
      if (isNil(polygonIds) || isEmpty(polygonIds)) {
        toast.error('Please draw or select at least one polygon');
        return;
      }

      // Export as MultiPolygon WKT.
      const multiPolygonWkt = polygonTool?.exportMultiPolygonWkt(polygonIds);

      if (isNil(multiPolygonWkt) || isEmpty(multiPolygonWkt)) {
        toast.error('Please draw or select at least one polygon');
        return;
      }

      payload.aoiGeometry = multiPolygonWkt;
      polygonCount = polygonIds.length;
    }

    trackEvent(Events.GenerateCleanupClicked, {
      [EventPropertyKey.ProjectName]: selectedProject?.name,
      [EventPropertyKey.SiteName]: selectedTerrainSite?.name,
      [EventPropertyKey.IterationName]: selectedTerrainIteration?.name,
      [EventPropertyKey.CleanupName]: values.cleanupName,
      [EventPropertyKey.AreaOfInterestType]: currentMarkedArea,
      [EventPropertyKey.PolygonCount]: polygonCount,
    });

    sendCreateTerrainCleanupRequest(payload);
  };

  const isGenerateCleanupDisabled =
    !values.cleanupName ||
    (currentMarkedArea === MarkArea.Draw && !hasPolygons) ||
    (currentMarkedArea === MarkArea.LayerFromWorkspace &&
      !values.workspaceLayer);

  return (
    <form className="terrain-cleanup__form" onSubmit={onSubmit}>
      <div className="terrain-cleanup__form__content map-3d-sidebar-content">
        <InputGroup>
          <Input.Label className="terrain-cleanup__form-item__title">
            Active Terrain
          </Input.Label>
          <Input.Text disabled value={selectedTerrainIteration?.name} />
        </InputGroup>
        <InputGroup>
          <Input.Label className="terrain-cleanup__form-item__title">
            Cleanup Name
          </Input.Label>
          <Input.Text
            value={values.cleanupName}
            name={names.cleanupName}
            error={errors.cleanupName}
            isInvalid={!!errors.cleanupName}
            disabled={!!errors.workspaceLayer || isCreatingTerrainCleanup}
            placeholder="Cleanup Name"
            {...{ onChange, onBlur, onFocus }}
          ></Input.Text>
        </InputGroup>
        <MarkAreaToggle
          currentMarkedArea={currentMarkedArea}
          onToggleChange={onToggleChange}
          drawingHandlers={{
            endDrawingHandler,
            startDrawingHandler,
            resetDrawingHandler,
          }}
          headerComponent={
            <Input.Label className="mark-area__heading">
              Mark Area of Interest
            </Input.Label>
          }
          isDrawingToolEnabled={isDrawPolygonToolEnabled}
          workspaceLayerError={errors.workspaceLayer}
          selectLayerSelectProps={{
            options: polygonLayersSelectOptions,
            value: values.workspaceLayer,
            name: names.workspaceLayer,
            isDisabled:
              isEmpty(polygonLayersSelectOptions) || isCreatingTerrainCleanup,
            onChange: (selectedLayer: SelectOption<WorkspaceLayer>) => {
              setValues({ ...values, workspaceLayer: selectedLayer });
            },
            onBlur,
            onFocus,
          }}
          isDisabled={isCreatingTerrainCleanup}
        />
      </div>
      <div className="terrain-cleanup__form__footer">
        <Button
          type="submit"
          disabled={isGenerateCleanupDisabled}
          isLoading={isCreatingTerrainCleanup}
        >
          {isCreatingTerrainCleanup ? 'Generating Cleanup' : 'Generate Cleanup'}
        </Button>
      </div>
    </form>
  );
};
